package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.*
import com.siriusxm.cmc.error
import com.siriusxm.cmc.info
import com.siriusxm.cmc.utils.*
import com.siriusxm.cmc.views.UberSearch
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.components.PageContents
import com.siriusxm.pia.components.LazyPaginationControl
import com.siriusxm.pia.operationsUser
import com.siriusxm.pia.rest.unifiedaggregator.Entity
import com.siriusxm.pia.rest.unifiedaggregator.asEntities
import unifiedcontent.aggregator.RepublishAction
import contentingestion.unifiedmodel.EntityType
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@OptIn(ExperimentalComposeWebApi::class)
object AggregatorStyles : StyleSheet() {
    val entityList by style {
        padding(0.px)
        margin(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap((0.8).cssRem)
        // No background - let items have their own backgrounds
    }

    val entityListSelected by style {
        padding(0.px)
        margin(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap((0.8).cssRem)
        backgroundColor(SXMUI.selectionBackgroundColor.value())
        padding(8.px)
        borderRadius(12.px)
    }

    val searchResultsScrollable by style {
        maxHeight(600.px)
        overflowY("auto")
    }

    val entityListIcons by style {}
    val entityListIcon by style {}

    /**
     * Style for entity list item image container.
     * Provides consistent sizing and scaling behavior matching EPG list items.
     */
    @OptIn(ExperimentalComposeWebApi::class)
    val entityListItemImage by style {
        position(Position.Relative)
        width(112.px)
        textAlign("center")

        transform {
            scale(.94)
        }

        transitions {
            "transform" {
                duration(0.12.s)
                timingFunction(AnimationTimingFunction.Ease)
            }
        }

        "img" {
            borderRadius(16.px)
            width(100.percent)
            display(DisplayStyle.Block)
        }
    }

    /**
     * Style for entity list items with card-based appearance.
     * Uses rounded corners, background color, and hover scale effect.
     */
    @OptIn(ExperimentalComposeWebApi::class)
    val entityListItem by style {
        position(Position.Relative)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Row)
        alignItems(AlignItems.Center)
        gap(8.px)
        padding((0.4).cssRem)
        backgroundColor(SXMUI.containerHeaderBackgroundColor.value())
        borderRadius(24.px)

        self + hover style {
            backgroundColor(SXMUI.containerContentBackgroundColor.value())
            textDecoration("none")
        }

        transitions {
            "transform" {
                duration(0.12.s)
                timingFunction(AnimationTimingFunction.Ease)
            }
        }

        className(entityListIcons) style {
            marginTop(4.px)
            display(DisplayStyle.Flex)
            flexWrap(FlexWrap.Wrap)
            gap(4.px)

            className(entityListIcon) style {

            }

        }

    }

    init {
        ".${entityListItem}:hover" style {
            transform { scale(1.01) }
            backgroundColor(SXMUI.containerContentBackgroundColor.value())
        }
    }

    val entitySubInfo by style {
        fontSize((0.8.cssRem))
        color(SXMUI.subtleTextColor.value())
        marginBottom(3.px)
    }

    /**
     * Style for the Slack icon shown next to a producer/consumer's Slack channel link.
     */
    val slackIcon by style {
        width(20.px)
        marginRight(.5.em)
        display(DisplayStyle.InlineBlock)
        property("vertical-align", "middle")
    }

    val imageBox by style {
        display(DisplayStyle.InlineBlock)
        padding(10.px)
    }

    val imageContainer by style {
        width(200.px)
        height(200.px)
        lineHeight(200.px)

        type("img") style {
            maxWidth(200.px)
            maxHeight(200.px)
            property("vertical-align", "middle")
        }
    }

    val imageError by style {
        width(200.px)
        height(200.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        backgroundColor(SXMUI.containerHeaderBackgroundColor.value())
        borderRadius(6.px)
        color(SXMUI.subtleTextColor.value())
        fontSize(12.px)
    }

    val imageFullscreenOverlay by style {
        position(Position.Fixed)
        top(0.px)
        left(0.px)
        bottom(0.px)
        right(0.px)
        width(100.percent)
        padding(20.px)
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        backgroundColor(rgba(0, 0, 0, .8))
        property("z-index", "100")
    }

    val imageFullscreenContent by style {
        background("repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 50% 50% / 20px 20px")
        property("width", "auto")
        property("height", "auto")
        maxWidth(100.percent)
        maxHeight(100.percent)
    }

    val imageLabel by style {
        padding(10.px)
        textAlign("center")
    }

    val paginationContainer by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        margin(1.em, 0.px)
    }
    val republishAllContainer by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        margin(1.em, 0.px)
    }

    val additionalNames by style {
        margin(3.px, 0.px)
        lastChild {
            marginBottom(0.em)
        }
    }

    val additionalName by style {
        marginBottom(.5.em)
    }

    val entityHeader by style {
        display(DisplayStyle.Flex)
        gap(10.px)

        whenNarrow {
            display(DisplayStyle.Block)
        }
    }

    val entityHeaderImage by style {
        width(100.percent)
        borderRadius(SXMUI.boxRadius.value())
        whenWiderThanPhone {
            width(150.px)
        }
    }
}

/**
 * Determines if the search input requires paginated results.
 * Returns false for single entity lookups (GUID, typed ID, ID lists).
 */
private fun isPaginatedSearch(input: String?): Boolean {
    if (input.isNullOrBlank()) return true
    if (isValidGuid(input)) return false
    if (input.isTypedEntityList() || input.isGuidList()) return false
    if (input.isCompositeEntityId()) return false
    val prefix = input.substringBefore(":", "")
    if (prefix.length in 2..4 && prefix.uppercase() == prefix) return false
    return true
}

@Composable
fun ServiceViewContext.entities(
    context: AggregatorService,
    initSearch: String? = null,
    initType: String? = null,
    initVisible: Boolean? = null
) {
    val userRoles by context.app.userRoles()

    // Track submitted search parameters (only changes on submit)
    var submittedQuery by remember { mutableStateOf(initSearch) }
    var submittedType by remember { mutableStateOf(initType) }
    var submittedVisible by remember { mutableStateOf(initVisible) }

    var results by remember { mutableStateOf<List<Entity>>(emptyList()) }
    val pageSize = 10
    val coroutineScope = rememberCoroutineScope()
    var allowRepublishAll by remember { mutableStateOf(false) }

    // For non-paginated searches (single entity lookups)
    var directResults by remember { mutableStateOf<List<Entity>?>(null) }
    var directLoading by remember { mutableStateOf(false) }

    // For paginated searches - track if we've done initial load
    var paginatedLoading by remember { mutableStateOf(false) }
    var paginatedSearchKey by remember { mutableStateOf<Triple<String?, String?, Boolean?>?>(null) }

    // Run direct (non-paginated) search for single entity lookups
    suspend fun runDirectSearch() {
        directLoading = true
        allowRepublishAll = false
        directResults = null

        try {
            if (isValidGuid(submittedQuery.orEmpty())) {
                val sourceId = context.clientApi.getMapping(submittedQuery!!).sourceId
                directResults = context.clientApi.getEntityById(sourceId, true).asEntities()
            } else if (submittedQuery.isTypedEntityList() || submittedQuery.isGuidList()) {
                val idList = if (submittedQuery.isTypedEntityList()) {
                    submittedQuery.toTypedEntityList()
                } else {
                    submittedQuery.toGuidList()?.map {
                        context.clientApi.getMapping(it).sourceId
                    }
                }
                directResults = coroutineScope {
                    idList?.map {
                        async {
                            context.clientApi.getEntityById(it).asEntities()
                        }
                    }?.awaitAll().orEmpty()
                }.flatten()
                if (context.viewer.operationsUser || userRoles?.isAnyAdmin() == true) {
                    allowRepublishAll = true
                }
            } else if (submittedQuery.isCompositeEntityId()) {
                val allowedTypes = submittedType?.split(",")?.map { it.trim() }?.filter { it.isNotBlank() }
                directResults = context.clientApi.getEntityById(submittedQuery!!, true).asEntities()
                    .filter { allowedTypes.isNullOrEmpty() || it.type in allowedTypes }
            } else {
                val prefix = submittedQuery?.substringBefore(":", "").orEmpty()
                if (prefix.length in 2..4 && prefix.uppercase() == prefix) {
                    directResults = context.clientApi.getEntityById(submittedQuery!!, true).asEntities()
                }
            }
        } catch (e: Exception) {
            e.printStackTrace()
            directResults = emptyList()
        } finally {
            directLoading = false
        }
    }

    // For non-paginated searches, run on init
    LaunchedEffect(initSearch, initType, initVisible) {
        submittedQuery = initSearch
        submittedType = initType
        submittedVisible = initVisible
        if (!isPaginatedSearch(initSearch)) {
            runDirectSearch()
        } else if (initSearch != null) {
            // For paginated search on init, set up the search key to trigger loading
            results = emptyList()
            paginatedLoading = true
            paginatedSearchKey = Triple(initSearch, initType, initVisible)
        }
    }

    serviceContentView({}) {
        // The aggregator home search reuses the CMC main home page's search panel (UberSearch):
        // a "Search" tab wrapping SmartSearchInput, with live lookahead, removable
        // type:/visibility: filter chips, and inline result previews. It navigates to
        // `aggregator/search?query=…` on submit, which re-renders this composable via the route and
        // drives the results rendered below. It is rendered bare (no `box`) to match the main home
        // page — the box's `overflow: hidden` would clip the suggestion/results dropdown.
        //
        // Seed the panel from the URL-derived search params so the field and filter chips reflect
        // the active search after the submit deep-links here (the search route is a different
        // routing branch than the default home, so the panel remounts and would otherwise come up
        // empty). `type` arrives as a comma-separated id list; split it back into chip ids.
        Div({ style { marginBottom(16.px) } }) {
            UberSearch(
                initialQuery = initSearch.orEmpty(),
                initialTypes = initType?.split(",")?.map { it.trim() }?.filter { it.isNotBlank() }
                    ?: emptyList(),
                initialVisibility = initVisible
            )
        }

        // Results displayed below the search box
        val isPaginated = isPaginatedSearch(submittedQuery)
        val isIdList = submittedQuery?.isTypedEntityList() == true || submittedQuery?.isGuidList() == true

        if (!isPaginated) {
            // Non-paginated results (single entity lookups)
            if (directLoading) {
                Div({
                    style {
                        padding(2.em)
                        textAlign("center")
                    }
                }) {
                    spinner()
                }
            } else if (directResults != null) {
                if (directResults!!.isEmpty()) {
                    boxMessage("No results found")
                } else {
                    entityList(context, directResults!!, includeIds = isIdList)
                    if (isIdList && allowRepublishAll) {
                        Div({ classes(AggregatorStyles.republishAllContainer) }) {
                            Button({
                                classes(ButtonStyles.button)
                                onClick {
                                    coroutineScope.launch {
                                        val published = directResults!!.map {
                                            async {
                                                val idMapping = context.clientApi.getMapping(it.id)
                                                try {
                                                    context.clientApi.republishEntity(
                                                        entityId = idMapping.atlasId,
                                                        entityType = EntityType.fromName(it.type),
                                                        entityVersion = it.version,
                                                        action = RepublishAction.republish,
                                                        updateTimeStamp = false
                                                    )
                                                    it.id
                                                } catch (t: Throwable) {
                                                    context.notifications.error("Republish failed id: ${it.id} message: ${t.message}")
                                                    null
                                                }
                                            }
                                        }.awaitAll().filterNotNull()

                                        if (published.isNotEmpty()) {
                                            context.notifications.info(
                                                "Republished ${published.size} of ${directResults!!.size} ids: ${
                                                    published.joinToString(", ")
                                                }"
                                            )
                                        }
                                    }
                                }
                            }) {
                                Text("Republish All")
                            }
                        }
                    }
                }
            }
        } else if (paginatedSearchKey != null) {
            // Paginated search results
            val query = submittedQuery?.takeIf { it.isNotBlank() }
            val types = submittedType?.let { listOf(it) }

            if (query != null || !types.isNullOrEmpty()) {
                // Pagination control at top right (always render to trigger fetching)
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        padding(0.5.cssRem, 0.cssRem)
                    }
                }) {
                    LazyPaginationControl<Entity>(
                        key = paginatedSearchKey,
                        updater = { items ->
                            results = items ?: emptyList()
                            paginatedLoading = false
                        },
                        fetcher = { token ->
                            val pageIndex = token?.toIntOrNull() ?: 0
                            val response = context.clientApi.searchEntities(
                                query = query,
                                types = types?.joinToString(","),
                                from = pageIndex * pageSize,
                                size = pageSize,
                                visible = submittedVisible
                            )
                            val entities = response.entities.asEntities()
                            val nextToken = if (response.paginationToken != null) {
                                (pageIndex + 1).toString()
                            } else null
                            PageContents(entities, nextToken)
                        },
                        onError = { e ->
                            e.printStackTrace()
                            paginatedLoading = false
                        }
                    )
                }

                // Show loading, no results, or results
                if (paginatedLoading) {
                    Div({
                        style {
                            padding(2.em)
                            textAlign("center")
                        }
                    }) {
                        spinner()
                    }
                } else if (results.isEmpty()) {
                    boxMessage("No results found")
                } else {
                    entityList(context, results)
                }
            }
        }
    }
}